import { api } from "@/lib/api";

export interface ArtifactVersionPayload {
  id?: string;
  version_number?: number;
  content: string;
  change_summary?: string | null;
  citations?: Record<string, any>[] | null;
}

export interface ArtifactVersionUpdatePayload {
  content?: string;
  change_summary?: string | null;
  citations?: Record<string, any>[] | null;
}

export interface ArtifactPayload {
  id?: string;
  project_id: string;
  title: string;
  type: string;
}

export interface ArtifactUpdatePayload {
  title?: string;
}

export interface ArtifactBulkInput {
  id: string;
  project_id: string;
  title: string;
  type: string;
  versions: ArtifactVersionPayload[];
}

const artifactCache = new Map<string, any>();
const artifactVersionCache = new Map<string, any>();
const artifactVersionsByArtifact = new Map<string, any[]>();
const prefetchedProjects = new Set<string>();

const cacheArtifact = (artifact: any) => {
  if (artifact?.id) {
    artifactCache.set(artifact.id, artifact);
  }
  if (Array.isArray(artifact?.versions)) {
    artifactVersionsByArtifact.set(artifact.id, artifact.versions);
    artifact.versions.forEach((version: any) => {
      if (version?.id) {
        artifactVersionCache.set(version.id, version);
      }
    });
  }
};

const cacheVersions = (artifactId: string, versions: any[]) => {
  artifactVersionsByArtifact.set(artifactId, versions);
  versions.forEach((version: any) => {
    if (version?.id) {
      artifactVersionCache.set(version.id, version);
    }
  });
};

export const artifactService = {
  listArtifacts: async (projectId?: string) => {
    const response = await api.get("/api/v1/artifacts", {
      params: { project_id: projectId },
    });
    const data = response.data as { artifacts: any[] };
    data.artifacts?.forEach(cacheArtifact);
    return data;
  },

  createArtifact: async (payload: ArtifactPayload) => {
    const response = await api.post("/api/v1/artifacts", payload);
    const data = response.data as any;
    cacheArtifact(data);
    return data;
  },

  getArtifact: async (artifactId: string) => {
    const cached = artifactCache.get(artifactId);
    if (cached) return cached;
    const response = await api.get(`/api/v1/artifacts/${artifactId}`);
    const data = response.data as any;
    cacheArtifact(data);
    return data;
  },

  updateArtifact: async (artifactId: string, payload: ArtifactUpdatePayload) => {
    const response = await api.patch(`/api/v1/artifacts/${artifactId}`, payload);
    const data = response.data as any;
    cacheArtifact(data);
    return data;
  },

  getArtifactVersion: async (versionId: string) => {
    const cached = artifactVersionCache.get(versionId);
    if (cached) return cached;
    const response = await api.get(`/api/v1/artifacts/versions/${versionId}`);
    const data = response.data as any;
    if (data?.id) {
      artifactVersionCache.set(data.id, data);
    }
    return data;
  },

  listVersions: async (artifactId: string, options?: { force?: boolean }) => {
    const cached = artifactVersionsByArtifact.get(artifactId);
    if (cached && !options?.force) return { versions: cached };
    const response = await api.get(`/api/v1/artifacts/${artifactId}/versions`);
    const data = response.data as { versions: any[] };
    if (Array.isArray(data.versions)) {
      cacheVersions(artifactId, data.versions);
    }
    return data;
  },

  createVersion: async (artifactId: string, payload: ArtifactVersionPayload) => {
    const response = await api.post(`/api/v1/artifacts/${artifactId}/versions`, payload);
    const data = response.data as any;
    if (data?.id) {
      artifactVersionCache.set(data.id, data);
    }
    if (data?.artifact_id) {
      const current = artifactVersionsByArtifact.get(data.artifact_id) ?? [];
      artifactVersionsByArtifact.set(data.artifact_id, [...current, data]);
    }
    return data;
  },

  updateVersion: async (versionId: string, payload: ArtifactVersionUpdatePayload) => {
    const response = await api.patch(`/api/v1/artifacts/versions/${versionId}`, payload);
    const data = response.data as any;
    if (data?.id) {
      artifactVersionCache.set(data.id, data);
    }
    if (data?.artifact_id) {
      const current = artifactVersionsByArtifact.get(data.artifact_id);
      if (current) {
        const updated = current.map((version) => (
          version.id === data.id ? { ...version, ...data } : version
        ));
        artifactVersionsByArtifact.set(data.artifact_id, updated);
      }
    }
    return data;
  },

  exportVersionToPdf: async (versionId: string) => {
    const response = await api.post(`/api/v1/artifacts/versions/${versionId}/export/pdf`);
    return response.data as { download_url: string; filename: string };
  },

  bulkImport: async (payload: { artifacts: ArtifactBulkInput[] }) => {
    const response = await api.post("/api/v1/artifacts/bulk-import", payload);
    return response.data as any;
  },

  prefetchProjectArtifacts: async (projectId: string) => {
    if (prefetchedProjects.has(projectId)) return;
    prefetchedProjects.add(projectId);
    try {
      const { artifacts } = await artifactService.listArtifacts(projectId);
      if (Array.isArray(artifacts)) {
        await Promise.all(
          artifacts.map((artifact) => artifactService.listVersions(artifact.id))
        );
      }
    } catch (error) {
      prefetchedProjects.delete(projectId);
      throw error;
    }
  },
};
